import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';

interface TaskRuntimeHandle {
  cancel?: () => Promise<void> | void;
  cleanup?: () => void;
  subscriptions: Set<Subscription>;
  abortControllers: Set<AbortController>;
}

@Injectable({
  providedIn: 'root'
})
export class BackgroundTaskRuntimeService {
  private readonly taskHandles = new Map<string, TaskRuntimeHandle>();

  registerTask(taskId: string): void {
    if (!this.taskHandles.has(taskId)) {
      this.taskHandles.set(taskId, {
        subscriptions: new Set<Subscription>(),
        abortControllers: new Set<AbortController>()
      });
    }
  }

  setCancelHandler(taskId: string, cancel: () => Promise<void> | void): void {
    const handle = this.ensureHandle(taskId);
    handle.cancel = cancel;
  }

  setCleanupHandler(taskId: string, cleanup: () => void): void {
    const handle = this.ensureHandle(taskId);
    handle.cleanup = cleanup;
  }

  attachSubscription(taskId: string, subscription: Subscription): void {
    const handle = this.ensureHandle(taskId);
    handle.subscriptions.add(subscription);
  }

  attachAbortController(taskId: string, abortController: AbortController): void {
    const handle = this.ensureHandle(taskId);
    handle.abortControllers.add(abortController);
  }

  async cancelTask(taskId: string): Promise<void> {
    const handle = this.taskHandles.get(taskId);
    if (!handle) {
      return;
    }

    await handle.cancel?.();
    this.cleanupTask(taskId);
  }

  cleanupTask(taskId: string): void {
    const handle = this.taskHandles.get(taskId);
    if (!handle) {
      return;
    }

    handle.subscriptions.forEach((subscription) => subscription.unsubscribe());
    handle.abortControllers.forEach((abortController) => abortController.abort());
    handle.cleanup?.();

    this.taskHandles.delete(taskId);
  }

  private ensureHandle(taskId: string): TaskRuntimeHandle {
    this.registerTask(taskId);

    return this.taskHandles.get(taskId)!;
  }
}
